import React, { useEffect, useMemo, useState } from 'react';
import { getAuth, signOut } from 'firebase/auth';
import {
    collection,
    documentId,
    getDocs,
    limit,
    onSnapshot,
    orderBy,
    query,
    startAfter,
} from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { db } from './firebase';
import './AppLogs.css';
import PortalHeader from './PortalHeader';

const RECORDS_PAGE_SIZE = 100;
const normalizeKey = (value) => String(value || '')
    .replace(/[^a-z0-9]/gi, '')
    .toLowerCase();
const HIDDEN_RECORD_JSON_KEYS = new Set([
    // Add record field names here as needed.
].map(normalizeKey));
const HIDDEN_METADATA_KEYS = new Set([
    'appId',
    'appName',
    'archiveFileCount',
    'archiveFormat',
    'firebaseStorageDownloadTokens',
    'schemaVersion',
].map(normalizeKey));
const LOG_DATE_METADATA_ORDER = new Map([
    ['logdatefrom', 0],
    ['logdateto', 1],
]);

const getMetadataValue = (metadata, keys) => {
    if (!metadata || typeof metadata !== 'object') return '';

    const normalizedKeys = new Set(keys.map(normalizeKey));
    const match = Object.entries(metadata).find(([key]) =>
        normalizedKeys.has(normalizeKey(key))
    );

    return match?.[1] ?? '';
};

const getAppName = (upload, app) => app?.appName || upload.appName ||
    getMetadataValue(upload.metadata, ['appName']);
const getAppIconUrl = (uploads, app) => app?.appIconUrl ||
    uploads.find((upload) => upload.appIconUrl)?.appIconUrl ||
    getMetadataValue(uploads[0]?.metadata, ['appIconUrl']);
const getUsername = (upload) => upload.username ||
    getMetadataValue(upload.metadata, ['username', 'userName']);
const getPlatform = (upload) => upload.platform ||
    getMetadataValue(upload.metadata, ['platform']);

const getVisibleRecordData = (record) => Object.fromEntries(
    Object.entries(record.data || {})
        .filter(([key]) => !HIDDEN_RECORD_JSON_KEYS.has(normalizeKey(key)))
        .sort(([left], [right]) => left.localeCompare(right, undefined, { sensitivity: 'base' }))
);

const timestampToMillis = (value) => {
    if (!value) return 0;
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (typeof value.toDate === 'function') return value.toDate().getTime();
    if (typeof value.seconds === 'number') return value.seconds * 1000;

    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
};

const formatTimestamp = (value) => {
    const milliseconds = timestampToMillis(value);
    return milliseconds ? new Date(milliseconds).toLocaleString() : 'Unknown time';
};

const displayValue = (value) => {
    if (value === null) return 'null';
    if (value === undefined || value === '') return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};

const getRecordTitle = (record) => {
    const payload = getVisibleRecordData(record).payload;

    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        return displayValue(payload);
    }

    const candidate = [
        payload.message,
        payload.error,
        payload.type,
        payload.name,
        payload.operation
    ].find((value) => ['string', 'number', 'boolean'].includes(typeof value));

    return candidate === undefined ? 'Record' : String(candidate);
};

const getRecordContext = (record) => {
    const payload = getVisibleRecordData(record).payload;

    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        return '';
    }

    const request = payload.request || payload.data || {};
    const operation = payload.endpoint || payload.path || request.endpoint || request.path;
    const method = payload.method || request.method;

    return [method, operation].filter(Boolean).join(' · ');
};

const getJsonValueSummary = (value) => Array.isArray(value) ?
    `Array (${value.length})` : `Object (${Object.keys(value).length})`;

function CollapsibleJsonField({ name, value, expansionCommand }) {
    const [expanded, setExpanded] = useState(false);
    const [childExpansionCommand, setChildExpansionCommand] = useState(expansionCommand);
    const isCollapsible = value !== null && typeof value === 'object';
    const hasNestedCollapsibleValue = isCollapsible && Object.values(value).some(
        (childValue) => childValue !== null && typeof childValue === 'object'
    );

    useEffect(() => {
        if (expansionCommand.revision > 0) {
            setExpanded(expansionCommand.expanded);
        }
        setChildExpansionCommand(expansionCommand);
    }, [expansionCommand]);

    const setSubtreeExpanded = (event, nextExpanded) => {
        event.preventDefault();
        event.stopPropagation();
        setExpanded(nextExpanded);
        setChildExpansionCommand((current) => ({
            expanded: nextExpanded,
            revision: current.revision + 1,
        }));
    };

    if (!isCollapsible) {
        return (
            <div className="app-log-json-scalar">
                <span>{JSON.stringify(name)}</span>: {JSON.stringify(value)}
            </div>
        );
    }

    return (
        <div className="app-log-json-field">
            <div className="app-log-json-node-header">
                <button
                    type="button"
                    className="app-log-json-node-toggle"
                    aria-expanded={expanded}
                    onClick={() => setExpanded((current) => !current)}
                >
                    <span className="app-log-json-node-chevron" aria-hidden="true">
                        {expanded ? '▾' : '▸'}
                    </span>
                    <span className="app-log-json-key">{JSON.stringify(name)}</span>
                    <small>{getJsonValueSummary(value)}</small>
                </button>
                {hasNestedCollapsibleValue ? (
                    <span className="app-log-json-node-actions">
                        <button
                            type="button"
                            onClick={(event) => setSubtreeExpanded(event, true)}
                        >
                            Expand all
                        </button>
                        <button
                            type="button"
                            onClick={(event) => setSubtreeExpanded(event, false)}
                        >
                            Collapse all
                        </button>
                    </span>
                ) : null}
            </div>
            {expanded ? (
                <div className="app-log-json-children">
                    {Object.entries(value).map(([childName, childValue]) => (
                        <CollapsibleJsonField
                            key={childName}
                            name={Array.isArray(value) ? `[${childName}]` : childName}
                            value={childValue}
                            expansionCommand={childExpansionCommand}
                        />
                    ))}
                </div>
            ) : null}
        </div>
    );
}

function CollapsibleRecordJson({ record }) {
    const [copyStatus, setCopyStatus] = useState('idle');
    const [expansionCommand, setExpansionCommand] = useState({
        expanded: false,
        revision: 0,
    });
    const visibleRecordData = getVisibleRecordData(record);

    const setAllExpanded = (expanded) => {
        setExpansionCommand((current) => ({
            expanded,
            revision: current.revision + 1,
        }));
    };

    const copyRecordJson = async () => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(visibleRecordData, null, 2));
            setCopyStatus('copied');
        } catch (copyError) {
            console.error('Unable to copy app log record:', copyError);
            setCopyStatus('failed');
        }
    };

    return (
        <div className="app-log-json">
            <div className="app-log-json-toolbar">
                <div>
                    <span>Record JSON</span>
                    <small>Drag the bottom edge to resize</small>
                </div>
                <div className="app-log-json-actions">
                    <button type="button" onClick={() => setAllExpanded(true)}>Expand all</button>
                    <button type="button" onClick={() => setAllExpanded(false)}>Collapse all</button>
                    <button type="button" onClick={copyRecordJson}>
                        {copyStatus === 'copied' ? 'Copied' :
                            copyStatus === 'failed' ? 'Copy failed' : 'Copy JSON'}
                    </button>
                </div>
            </div>
            {Object.entries(visibleRecordData).map(([name, value]) => (
                <CollapsibleJsonField
                    key={name}
                    name={name}
                    value={value}
                    expansionCommand={expansionCommand}
                />
            ))}
        </div>
    );
}

function JsonRecord({ record }) {
    const [expanded, setExpanded] = useState(false);

    return (
        <article className={`app-log-record ${expanded ? 'expanded' : ''}`}>
            <button
                type="button"
                className="app-log-record-toggle"
                onClick={() => setExpanded((current) => !current)}
                aria-expanded={expanded}
            >
                <span className="app-log-record-line">#{record.lineNumber}</span>
                <span className="app-log-record-copy">
                    <strong>{getRecordTitle(record)}</strong>
                    {getRecordContext(record) ? <small>{getRecordContext(record)}</small> : null}
                </span>
                <span className="app-log-chevron" aria-hidden="true">
                    {expanded ? '−' : '+'}
                </span>
            </button>

            {expanded ? <CollapsibleRecordJson record={record} /> : null}
        </article>
    );
}

function AppMark({ iconUrl, appName }) {
    const [imageFailed, setImageFailed] = useState(false);

    if (!iconUrl || imageFailed) {
        return <span className="app-log-app-mark">APP</span>;
    }

    return (
        <span className="app-log-app-mark app-log-app-icon">
            <img
                src={iconUrl}
                alt={`${appName || 'App'} icon`}
                onError={() => setImageFailed(true)}
            />
        </span>
    );
}

function AppleIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M15.7 3.2c.1 1-.3 2-1 2.7-.7.7-1.7 1.2-2.7 1.1-.1-1 .3-2 1-2.7.7-.7 1.8-1.2 2.7-1.1ZM19.1 16.8c-.4.9-.9 1.8-1.6 2.6-.8 1-1.7 2.2-3.1 2.2-1.2 0-1.6-.8-3-.8s-1.9.8-3.1.8c-1.3 0-2.2-1.1-3-2.1C3 16.7 2.8 13.4 4.2 11.2c1-1.6 2.7-2.6 4.5-2.6 1.3 0 2.5.8 3.1.8.6 0 2.1-1 3.6-.8.6 0 2.5.2 3.7 2-2.2 1.3-1.9 4.5 0 6.2Z" />
        </svg>
    );
}

function AndroidIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="m8.1 7-1.4-2M15.9 7l1.4-2M6.5 10.5h11v7.2a1.8 1.8 0 0 1-1.8 1.8H8.3a1.8 1.8 0 0 1-1.8-1.8v-7.2ZM6.5 10.5a5.5 5.5 0 0 1 11 0" />
            <circle cx="9.3" cy="8.4" r=".7" />
            <circle cx="14.7" cy="8.4" r=".7" />
        </svg>
    );
}

function PlatformTag({ platform }) {
    const normalizedPlatform = String(platform || '').trim().toLowerCase();

    if (!normalizedPlatform) return null;

    if (normalizedPlatform.includes('android')) {
        return <span className="app-log-platform-tag android"><AndroidIcon />Android</span>;
    }

    if (normalizedPlatform.includes('ios') || normalizedPlatform.includes('apple')) {
        return <span className="app-log-platform-tag ios"><AppleIcon />iOS</span>;
    }

    return <span className="app-log-platform-tag">{platform}</span>;
}

function DateEntries({ dateEntry, uploadId }) {
    const [expanded, setExpanded] = useState(false);
    const [records, setRecords] = useState([]);
    const [lastRecord, setLastRecord] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [loadedOnce, setLoadedOnce] = useState(false);

    const loadRecords = async () => {
        if (loading || !hasMore) return;

        setLoading(true);
        setError('');

        try {
            const recordsRef = collection(
                db,
                'AppLogs',
                uploadId,
                'Entries',
                dateEntry.id,
                'Records'
            );
            const constraints = [orderBy(documentId(), 'desc'), limit(RECORDS_PAGE_SIZE)];

            if (lastRecord) constraints.push(startAfter(lastRecord));

            const snapshot = await getDocs(query(recordsRef, ...constraints));
            const nextRecords = snapshot.docs.map((recordDoc) => ({
                id: recordDoc.id,
                ...recordDoc.data(),
            }));

            const loadedRecordCount = records.length + nextRecords.length;
            const expectedRecordCount = Number(dateEntry.recordCount || 0);

            setRecords((current) => [...current, ...nextRecords]);
            setLastRecord(snapshot.docs[snapshot.docs.length - 1] || null);
            setHasMore(expectedRecordCount ?
                loadedRecordCount < expectedRecordCount :
                snapshot.docs.length === RECORDS_PAGE_SIZE);
            setLoadedOnce(true);
        } catch (loadError) {
            console.error('Unable to load app log records:', loadError);
            setError('Unable to load records for this date. Try again or contact the FieldOps team if the issue persists.');
        } finally {
            setLoading(false);
        }
    };

    const toggleExpanded = () => {
        const nextExpanded = !expanded;
        setExpanded(nextExpanded);

        if (nextExpanded && !loadedOnce) {
            loadRecords();
        }
    };

    const remainingCount = Math.max((dateEntry.recordCount || 0) - records.length, 0);

    const displayDate = dateEntry.date || dateEntry.id;

    return (
        <section className="app-log-date-card">
            <button
                type="button"
                className="app-log-date-toggle"
                onClick={toggleExpanded}
                aria-expanded={expanded}
            >
                <span className="app-log-date-calendar" aria-hidden="true">{displayDate.slice(-2)}</span>
                <span className="app-log-date-copy">
                    <strong>{displayDate}</strong>
                    <small>{dateEntry.recordCount || 0} records</small>
                </span>
                <span className="app-log-chevron" aria-hidden="true">
                    {expanded ? '−' : '+'}
                </span>
            </button>

            {expanded ? (
                <div className="app-log-date-content">
                    {error ? <p className="app-log-error">{error}</p> : null}

                    <div className="app-log-record-list">
                        {records.map((record) => (
                            <JsonRecord key={record.id} record={record} />
                        ))}
                    </div>

                    {loading ? <div className="app-log-inline-loading">Loading records…</div> : null}

                    {!loading && records.length === 0 && loadedOnce && !error ? (
                        <p className="app-log-empty-inline">No records found for this date.</p>
                    ) : null}

                    {!loading && hasMore ? (
                        <button type="button" className="app-log-load-more" onClick={loadRecords}>
                            Load {Math.min(remainingCount || RECORDS_PAGE_SIZE, RECORDS_PAGE_SIZE)} more
                        </button>
                    ) : null}
                </div>
            ) : null}
        </section>
    );
}

function SupportCodeDetails({ upload }) {
    const [dateEntries, setDateEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const entriesRef = collection(db, 'AppLogs', upload.id, 'Entries');
        const unsubscribe = onSnapshot(entriesRef, (snapshot) => {
            const nextEntries = snapshot.docs
                .map((entryDoc) => ({ id: entryDoc.id, ...entryDoc.data() }))
                .sort((left, right) => right.id.localeCompare(left.id));

            setDateEntries(nextEntries);
            setLoading(false);
        }, (loadError) => {
            console.error('Unable to load app log dates:', loadError);
            setError('Unable to load records for this support code.');
            setLoading(false);
        });

        return unsubscribe;
    }, [upload.id]);

    const metadata = upload.metadata && typeof upload.metadata === 'object' ? upload.metadata : {};
    const metadataEntries = Object.entries(metadata)
        .filter(([key]) => !HIDDEN_METADATA_KEYS.has(normalizeKey(key)))
        .sort(([left], [right]) => {
            const leftDateOrder = LOG_DATE_METADATA_ORDER.get(normalizeKey(left));
            const rightDateOrder = LOG_DATE_METADATA_ORDER.get(normalizeKey(right));

            if (leftDateOrder !== undefined || rightDateOrder !== undefined) {
                if (leftDateOrder === undefined) return 1;
                if (rightDateOrder === undefined) return -1;
                return leftDateOrder - rightDateOrder;
            }

            return left.localeCompare(right);
        });

    return (
        <div className="app-log-support-content">
            <section className="app-log-metadata-panel">
                <div className="app-log-section-heading">
                    <div>
                        <p className="app-log-section-kicker">Client context</p>
                        <h4>Upload metadata</h4>
                    </div>
                    <span>{metadataEntries.length} fields</span>
                </div>

                {metadataEntries.length ? (
                    <dl className="app-log-metadata-grid">
                        {metadataEntries.map(([key, value]) => (
                            <div key={key}>
                                <dt>{key}</dt>
                                <dd>{displayValue(value)}</dd>
                            </div>
                        ))}
                    </dl>
                ) : (
                    <p className="app-log-empty-inline">No additional upload metadata is available.</p>
                )}
            </section>

            <section className="app-log-days-panel">
                <div className="app-log-section-heading">
                    <div>
                        <p className="app-log-section-kicker">Records</p>
                        <h4>Records by date</h4>
                    </div>
                    <span>{dateEntries.length} days</span>
                </div>

                {loading ? <div className="app-log-inline-loading">Loading dates…</div> : null}
                {error ? <p className="app-log-error">{error}</p> : null}
                {!loading && !error && dateEntries.length === 0 ? (
                    <p className="app-log-empty-inline">No records were found.</p>
                ) : null}

                <div className="app-log-date-list">
                    {dateEntries.map((dateEntry) => (
                        <DateEntries key={dateEntry.id} dateEntry={dateEntry} uploadId={upload.id} />
                    ))}
                </div>
            </section>
        </div>
    );
}

function AppLogs() {
    const [uploads, setUploads] = useState([]);
    const [appsById, setAppsById] = useState(() => new Map());
    const [expandedApps, setExpandedApps] = useState([]);
    const [expandedSupports, setExpandedSupports] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const unsubscribe = onSnapshot(collection(db, 'AppLogs'), (snapshot) => {
            const nextUploads = snapshot.docs
                .map((uploadDoc) => ({ id: uploadDoc.id, ...uploadDoc.data() }))
                .filter((upload) => upload.status === 'ready')
                .sort((left, right) => timestampToMillis(right.processedAt) - timestampToMillis(left.processedAt));

            setUploads(nextUploads);
            setLoading(false);
        }, (loadError) => {
            console.error('Unable to load app log uploads:', loadError);
            setError('Unable to load app logs. Refresh the page or contact the FieldOps team if the issue persists.');
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    useEffect(() => {
        const unsubscribe = onSnapshot(collection(db, 'App'), (snapshot) => {
            const nextAppsById = new Map();

            snapshot.docs.forEach((appDoc) => {
                const app = appDoc.data();
                nextAppsById.set(String(app.appId || appDoc.id), app);
            });

            setAppsById(nextAppsById);
        }, (loadError) => {
            console.error('Unable to load app names and icons:', loadError);
        });

        return unsubscribe;
    }, []);

    const groupedUploads = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();
        const filteredUploads = uploads.filter((upload) => {
            if (!normalizedSearch) return true;

            return [
                upload.appId,
                upload.supportCode,
                JSON.stringify(upload.metadata || {}),
            ].some((value) => String(value || '').toLowerCase().includes(normalizedSearch));
        });
        const groups = new Map();

        filteredUploads.forEach((upload) => {
            const appId = upload.appId || 'Unknown app';
            if (!groups.has(appId)) groups.set(appId, []);
            groups.get(appId).push(upload);
        });

        return [...groups.entries()].sort(([left], [right]) => left.localeCompare(right));
    }, [search, uploads]);

    const toggleItem = (setter, id) => {
        setter((current) => current.includes(id) ?
            current.filter((item) => item !== id) : [...current, id]);
    };

    const handleLogout = async () => {
        try {
            await signOut(getAuth());
            window.location.href = '/login';
        } catch (logoutError) {
            console.error('Error signing out:', logoutError);
        }
    };

    const totalRecords = uploads.reduce((sum, upload) => sum + (upload.recordCount || 0), 0);

    return (
        <div className="app-log-page">
            <PortalHeader>
                <nav className="app-log-nav" aria-label="Logs navigation">
                    <Link to="/legacy-logs">Legacy logs</Link>
                    <button type="button" onClick={handleLogout}>Sign out</button>
                </nav>
            </PortalHeader>

            <main className="app-log-main">
                <section className="app-log-hero">
                    <div>
                        <p className="app-log-eyebrow">Logs explorer</p>
                        <div className="app-log-retention-notice">
                            <strong>30-day retention</strong>
                            <span>Logs are available for 30 days after upload.</span>
                        </div>
                        <h1>App logs</h1>
                        <p>
                            Investigate support requests using app details, upload metadata, and
                            individual log records.
                        </p>
                    </div>
                    <div className="app-log-summary">
                        <div><strong>{groupedUploads.length}</strong><span>Apps</span></div>
                        <div><strong>{uploads.length}</strong><span>Requests</span></div>
                        <div><strong>{totalRecords.toLocaleString()}</strong><span>Records</span></div>
                    </div>
                </section>

                <section className="app-log-toolbar">
                    <label htmlFor="app-log-search">Search logs</label>
                    <div className="app-log-search-row">
                        <input
                            id="app-log-search"
                            type="search"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="Search app ID, support code, or metadata"
                        />
                        {search ? (
                            <button type="button" onClick={() => setSearch('')}>Clear</button>
                        ) : null}
                    </div>
                </section>

                {loading ? <div className="app-log-loading">Loading app logs…</div> : null}
                {error ? <div className="app-log-error-state"><h2>Logs unavailable</h2><p>{error}</p></div> : null}
                {!loading && !error && groupedUploads.length === 0 ? (
                    <div className="app-log-empty-state">
                        <h2>No app logs found</h2>
                        <p>{search ? 'Try a different search.' : 'Processed uploads will appear here.'}</p>
                    </div>
                ) : null}

                <section className="app-log-app-list">
                    {groupedUploads.map(([appId, appUploads]) => {
                        const appExpanded = expandedApps.includes(appId);
                        const app = appsById.get(String(appId));
                        const appName = getAppName(appUploads[0], app);
                        const appIconUrl = getAppIconUrl(appUploads, app);
                        const appRecordCount = appUploads.reduce(
                            (sum, upload) => sum + (upload.recordCount || 0),
                            0
                        );

                        return (
                            <article key={appId} className="app-log-app-card">
                                <button
                                    type="button"
                                    className="app-log-app-toggle"
                                    onClick={() => toggleItem(setExpandedApps, appId)}
                                    aria-expanded={appExpanded}
                                >
                                    <AppMark
                                        key={appIconUrl || 'fallback'}
                                        iconUrl={appIconUrl}
                                        appName={appName || appId}
                                    />
                                    <span className="app-log-app-copy">
                                        <strong>{appName ? `${appId} - ${appName}` : appId}</strong>
                                        <small>
                                            {appUploads.length} support {appUploads.length === 1 ? 'request' : 'requests'}
                                            {' · '}{appRecordCount.toLocaleString()} records
                                        </small>
                                    </span>
                                    <span className="app-log-chevron" aria-hidden="true">
                                        {appExpanded ? '−' : '+'}
                                    </span>
                                </button>

                                {appExpanded ? (
                                    <div className="app-log-support-list">
                                        {appUploads.map((upload) => {
                                            const supportExpanded = expandedSupports.includes(upload.id);
                                            const username = getUsername(upload);
                                            const platform = getPlatform(upload);

                                            return (
                                                <article key={upload.id} className="app-log-support-card">
                                                    <button
                                                        type="button"
                                                        className="app-log-support-toggle"
                                                        onClick={() => toggleItem(setExpandedSupports, upload.id)}
                                                        aria-expanded={supportExpanded}
                                                    >
                                                        <span className="app-log-status-dot" aria-hidden="true" />
                                                        <span className="app-log-support-copy">
                                                            <span>Support code</span>
                                                            <span className="app-log-support-title-row">
                                                                <strong className="app-log-support-code">
                                                                    {upload.supportCode || upload.id}
                                                                </strong>
                                                                <PlatformTag platform={platform} />
                                                            </span>
                                                            <small>
                                                                {displayValue(username || 'Unknown user')}
                                                                {' · '}{upload.recordCount || 0} records
                                                                {' · '}Uploaded {formatTimestamp(upload.uploadedAt)}
                                                            </small>
                                                        </span>
                                                        <span className="app-log-chevron" aria-hidden="true">
                                                            {supportExpanded ? '−' : '+'}
                                                        </span>
                                                    </button>

                                                    {supportExpanded ? <SupportCodeDetails upload={upload} /> : null}
                                                </article>
                                            );
                                        })}
                                    </div>
                                ) : null}
                            </article>
                        );
                    })}
                </section>
            </main>
        </div>
    );
}

export default AppLogs;
